import { useState, useEffect, useRef } from 'react';
import { CustomFont } from '@agency-smm/shared';
import { useBrandContext } from './brandShared';
import {
  SectionGrid,
  FormGroup,
  SectionHeaderRow,
  AiFillButton,
  SwatchRow,
  ColorSwatch,
  ColorPickerInput,
  AddButton,
  AddColorRow,
  FontTag,
  FontTagRemove,
  HintText,
  SliderRow,
  SliderLabelRow,
  SliderLabel,
  SliderHint,
  SliderValue,
  RangeSlider,
  Advanced,
  SavedTick,
  SubSection,
  SubSectionTitle,
} from './brandShared';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../Card';
import { Input, InputWrapper, Label, ErrorMessage } from '../Input';
import { Button } from '../Button';
import { apiClient } from '../../services/apiClient';
import { loadCustomFonts } from '../../utils/fontLoader';
import {
  FontFileRow,
  FontFileSample,
  FontFileMeta,
  DeleteFontBtn,
  HiddenFileInput,
  SwatchWrap,
  SwatchRemoveBtn,
  UploadFontRow,
  WeightSelect,
  ToggleRow,
  ToggleCheckbox,
  SavedRow,
} from './visualStyled';

/**
 * Tab Visual & Logo: brand colors + brand fonts (free-text), catálogo global de
 * custom fonts (upload/delete), controles de render del logo (scale + offset) y
 * el toggle forceBrandColors. Toda la fuente de verdad de colores/fonts vive en
 * el form; logo/forceBrandColors viven en live-persist (guardado inmediato).
 */
export function BrandTabVisual() {
  const ctx = useBrandContext();
  const { form, live, workspace, assets, isAdmin } = ctx;
  const { formData, setField } = form;

  const brandColors = formData.brandColors || [];
  const brandFonts = formData.brandFonts || [];

  // ─── Brand colors ──────────────────────────────────────────────────────────
  const [colorPickerValue, setColorPickerValue] = useState('#3B82F6');

  const addBrandColor = () => {
    // Dedup exacto + cap 6. El picker no distingue mayúsculas (siempre #hex).
    if (!colorPickerValue || brandColors.includes(colorPickerValue)) return;
    if (brandColors.length >= 6) return;
    setField('brandColors', [...brandColors, colorPickerValue]);
  };

  const removeBrandColor = (index: number) => {
    setField('brandColors', brandColors.filter((_, i) => i !== index));
  };

  // ─── Brand fonts (nombres de fuente en texto libre, max 3) ──────────────────
  const [fontInput, setFontInput] = useState('');

  const addBrandFont = () => {
    const trimmed = fontInput.trim();
    if (!trimmed || brandFonts.includes(trimmed) || brandFonts.length >= 3) return;
    setField('brandFonts', [...brandFonts, trimmed]);
    setFontInput('');
  };

  const removeBrandFont = (index: number) => {
    setField('brandFonts', brandFonts.filter((_, i) => i !== index));
  };

  // ─── Catálogo global de custom fonts (estado local; vive en /api/fonts) ─────
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [newFontFamily, setNewFontFamily] = useState('');
  const [newFontWeight, setNewFontWeight] = useState('400');
  const [isUploadingFont, setIsUploadingFont] = useState(false);
  const [fontUploadError, setFontUploadError] = useState('');
  const fontFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Fonts viven en una librería global → endpoint workspace-agnóstico.
    let cancelled = false;
    (async () => {
      try {
        const fonts = await apiClient.get<CustomFont[]>('/api/fonts');
        if (cancelled) return;
        setCustomFonts(fonts);
        // Registrarlas con el documento para que el sample row las renderice.
        void loadCustomFonts(fonts);
      } catch (err) {
        console.warn('Failed to fetch custom fonts:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFontFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const family = newFontFamily.trim();
    if (!family) {
      setFontUploadError('Enter the font name before choosing the file.');
      e.target.value = '';
      return;
    }

    setIsUploadingFont(true);
    setFontUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('family', family);
      fd.append('weight', newFontWeight);
      const created = await apiClient.upload<CustomFont>('/api/fonts/upload', fd);
      setCustomFonts((prev) => [...prev, created]);
      void loadCustomFonts([created]);
      setNewFontFamily('');
      setNewFontWeight('400');
    } catch (err) {
      setFontUploadError(err instanceof Error ? err.message : 'Font upload failed');
    } finally {
      setIsUploadingFont(false);
      e.target.value = ''; // reset para que re-subir el mismo archivo dispare onChange
    }
  };

  const handleDeleteFont = async (fontId: string) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this font from the global catalog? It affects ALL workspaces.')) {
      return;
    }
    try {
      await apiClient.delete(`/api/fonts/${fontId}`);
      setCustomFonts((prev) => prev.filter((f) => f.id !== fontId));
    } catch (err) {
      setFontUploadError(err instanceof Error ? err.message : 'Could not delete the font');
    }
  };

  // ─── Logo render controls ────────────────────────────────────────────────────
  // Solo cuando hay logo: o el workspace tiene logoUrl, o algún asset es el logo.
  const hasLogo = !!workspace?.logoUrl || assets.assets.some((a) => a.isLogo);

  return (
    <SectionGrid>
      {/* Brand Visual Identity — colores + fonts (+ catálogo en Advanced) */}
      <Card>
        <CardHeader>
          <SectionHeaderRow>
            <div>
              <CardTitle>Brand Visual Identity</CardTitle>
              <CardDescription>Colors and fonts that represent your brand</CardDescription>
            </div>
            <AiFillButton
              type="button"
              onClick={() => form.openAiFill('visual')}
              title="Extract colors and fonts from the site with AI"
            >
              <span aria-hidden="true">✨</span> AI
            </AiFillButton>
          </SectionHeaderRow>
        </CardHeader>
        <CardContent>
          {/* Brand Colors (max 6, × on each swatch to remove) */}
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-color-picker">Brand Colors (max 6)</Label>
              <SwatchRow>
                {brandColors.map((color, i) => (
                  <SwatchWrap key={i}>
                    <ColorSwatch
                      $color={color}
                      title={color}
                      onClick={() => removeBrandColor(i)}
                    />
                    <SwatchRemoveBtn
                      type="button"
                      aria-label={`Remove color ${color}`}
                      onClick={() => removeBrandColor(i)}
                    >
                      <span aria-hidden="true">×</span>
                    </SwatchRemoveBtn>
                  </SwatchWrap>
                ))}
                {brandColors.length < 6 && (
                  <AddColorRow>
                    <ColorPickerInput
                      id="brand-color-picker"
                      type="color"
                      value={colorPickerValue}
                      onChange={(e) => setColorPickerValue(e.target.value)}
                    />
                    <AddButton type="button" onClick={addBrandColor}>
                      + Add
                    </AddButton>
                  </AddColorRow>
                )}
              </SwatchRow>
              {brandColors.length === 0 && (
                <HintText>
                  No brand colors set. Add colors to use in templates with Brand color mode.
                </HintText>
              )}
            </InputWrapper>
          </FormGroup>

          {/* Brand Fonts (max 3, free-text names) */}
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-font-input">Brand Fonts (max 3)</Label>
              <SwatchRow>
                {brandFonts.map((font, i) => (
                  <FontTag key={i}>
                    {font}
                    <FontTagRemove
                      type="button"
                      aria-label={`Remove font ${font}`}
                      onClick={() => removeBrandFont(i)}
                    >
                      <span aria-hidden="true">×</span>
                    </FontTagRemove>
                  </FontTag>
                ))}
              </SwatchRow>
              {brandFonts.length < 3 && (
                <AddColorRow style={{ marginTop: 8 }}>
                  <Input
                    id="brand-font-input"
                    value={fontInput}
                    onChange={(e) => setFontInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addBrandFont();
                      }
                    }}
                    placeholder="e.g., Poppins, Montserrat..."
                    style={{ flex: 1 }}
                  />
                  <AddButton type="button" onClick={addBrandFont}>
                    + Add
                  </AddButton>
                </AddColorRow>
              )}
              {brandFonts.length === 0 && (
                <HintText>No brand fonts set. Add font names used in your brand guidelines.</HintText>
              )}
            </InputWrapper>
          </FormGroup>

          {/* Custom fonts (catálogo global) — campo avanzado/orphan. */}
          <Advanced>
            <summary>Custom fonts (global catalog)</summary>
            <div>
              <HintText style={{ marginTop: 0, marginBottom: 8 }}>
                Upload .woff2, .ttf or .otf for licensed fonts (e.g., Gotham, Avenir). Anyone can
                add them; they become available to <strong>all workspaces</strong>.
                {!isAdmin && ' Only the admin can delete them.'}
              </HintText>

              {customFonts.map((f) => (
                <FontFileRow key={f.id}>
                  <FontFileSample style={{ fontFamily: `"${f.family}", system-ui` }}>
                    {f.family}
                    <FontFileMeta>
                      weight {f.weight} · {f.style}
                      {f.fileSize ? ` · ${Math.round(f.fileSize / 1024)} KB` : ''}
                      {f.format ? ` · ${f.format}` : ''}
                    </FontFileMeta>
                  </FontFileSample>
                  {isAdmin && (
                    <DeleteFontBtn
                      type="button"
                      onClick={() => handleDeleteFont(f.id)}
                      title="Delete font from the global catalog"
                      aria-label={`Delete font ${f.family} from the global catalog`}
                    >
                      <span aria-hidden="true">×</span>
                    </DeleteFontBtn>
                  )}
                </FontFileRow>
              ))}

              <UploadFontRow>
                <div>
                  <Label htmlFor="new-font-family" style={{ fontSize: 11 }}>
                    Font name
                  </Label>
                  <Input
                    id="new-font-family"
                    value={newFontFamily}
                    onChange={(e) => setNewFontFamily(e.target.value)}
                    placeholder="e.g., Gotham"
                    disabled={isUploadingFont}
                  />
                </div>
                <div>
                  <Label htmlFor="new-font-weight" style={{ fontSize: 11 }}>
                    Weight
                  </Label>
                  <WeightSelect
                    id="new-font-weight"
                    value={newFontWeight}
                    onChange={(e) => setNewFontWeight(e.target.value)}
                    disabled={isUploadingFont}
                  >
                    <option value="100">100 Thin</option>
                    <option value="300">300 Light</option>
                    <option value="400">400 Regular</option>
                    <option value="500">500 Medium</option>
                    <option value="600">600 Semibold</option>
                    <option value="700">700 Bold</option>
                    <option value="800">800 Extra Bold</option>
                    <option value="900">900 Black</option>
                  </WeightSelect>
                </div>
                <div>
                  <Button
                    $size="sm"
                    onClick={() => fontFileInputRef.current?.click()}
                    disabled={isUploadingFont || !newFontFamily.trim()}
                  >
                    {isUploadingFont ? 'Uploading…' : 'Choose file'}
                  </Button>
                  <HiddenFileInput
                    ref={fontFileInputRef}
                    type="file"
                    accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                    onChange={handleFontFilePicked}
                  />
                </div>
              </UploadFontRow>
              {fontUploadError && <ErrorMessage style={{ marginTop: 8 }}>{fontUploadError}</ErrorMessage>}
            </div>
          </Advanced>
        </CardContent>
      </Card>

      {/* Logo rendering + forceBrandColors */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Logo &amp; Color Rules</CardTitle>
            <CardDescription>How your logo and brand colors apply to templates</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {/* Controles de render del logo — solo si hay logo. */}
          {hasLogo ? (
            <SubSection>
              <SubSectionTitle>Logo rendering</SubSectionTitle>

              <FormGroup>
                <SliderRow>
                  <SliderLabelRow>
                    <SliderLabel>Logo Size</SliderLabel>
                    <SliderValue>{(live.logoScale * 100).toFixed(0)}%</SliderValue>
                  </SliderLabelRow>
                  <RangeSlider
                    type="range"
                    min="0.3"
                    max="3.0"
                    step="0.1"
                    value={live.logoScale}
                    onChange={(e) => live.setLogoScale(parseFloat(e.target.value))}
                  />
                  <SliderHint>Adjust logo size in generated post images</SliderHint>
                  <SavedRow>
                    {live.savedField === 'logoScale' && <SavedTick>✓ Saved</SavedTick>}
                  </SavedRow>
                </SliderRow>
              </FormGroup>

              <FormGroup>
                <SliderRow>
                  <SliderLabelRow>
                    <SliderLabel>Horizontal Offset</SliderLabel>
                    <SliderValue>{live.logoOffsetX.toFixed(0)}%</SliderValue>
                  </SliderLabelRow>
                  <RangeSlider
                    type="range"
                    min="-50"
                    max="50"
                    step="1"
                    value={live.logoOffsetX}
                    onChange={(e) => live.setLogoOffset(parseFloat(e.target.value), live.logoOffsetY)}
                  />
                  <SavedRow>
                    {live.savedField === 'logoOffsetX' && <SavedTick>✓ Saved</SavedTick>}
                  </SavedRow>
                </SliderRow>
              </FormGroup>

              <FormGroup>
                <SliderRow>
                  <SliderLabelRow>
                    <SliderLabel>Vertical Offset</SliderLabel>
                    <SliderValue>{live.logoOffsetY.toFixed(0)}%</SliderValue>
                  </SliderLabelRow>
                  <RangeSlider
                    type="range"
                    min="-50"
                    max="50"
                    step="1"
                    value={live.logoOffsetY}
                    onChange={(e) => live.setLogoOffset(live.logoOffsetX, parseFloat(e.target.value))}
                  />
                  <SliderHint>Shift logo position across all templates</SliderHint>
                  <SavedRow>
                    {live.savedField === 'logoOffsetY' && <SavedTick>✓ Saved</SavedTick>}
                  </SavedRow>
                </SliderRow>
              </FormGroup>
            </SubSection>
          ) : (
            <HintText style={{ marginTop: 0 }}>
              Set a logo in the Assets tab to unlock logo size and position controls.
            </HintText>
          )}

          {/* forceBrandColors — siempre visible. */}
          <SubSection>
            <SubSectionTitle>Brand colors on templates</SubSectionTitle>
            <ToggleRow>
              <ToggleCheckbox
                type="checkbox"
                checked={live.forceBrandColors}
                onChange={(e) => live.setForceBrandColors(e.target.checked)}
              />
              <span>
                <SliderLabel as="span" style={{ display: 'block' }}>
                  Use my brand colors on all templates
                </SliderLabel>
                <SliderHint>
                  By default each template keeps its own palette. Turn this on so your brand colors
                  also apply to fixed-palette templates.
                </SliderHint>
              </span>
            </ToggleRow>
            <SavedRow>
              {live.savedField === 'forceBrandColors' && <SavedTick>✓ Saved</SavedTick>}
            </SavedRow>
          </SubSection>
        </CardContent>
      </Card>
    </SectionGrid>
  );
}

export default BrandTabVisual;
